// Family Discoveries — milestone birthdays, remembrance days & event search.
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Cake, Flower2, Search, Plus, Calendar } from "lucide-react";

const MS_DAY = 86400000;
function nextOccurrence(dateStr) {
  if (!dateStr || dateStr.length < 8) return null;
  const [, mm, dd] = dateStr.split("-");
  if (!mm || !dd) return null;
  const now = new Date(); now.setHours(0, 0, 0, 0);
  let next = new Date(now.getFullYear(), Number(mm) - 1, Number(dd));
  if (next < now) next = new Date(now.getFullYear() + 1, Number(mm) - 1, Number(dd));
  return { date: next, days: Math.round((next - now) / MS_DAY) };
}
const fmt = (d) => d.toLocaleDateString("en-IN", { day: "numeric", month: "long" });
const MILESTONES = new Set([1, 16, 18, 21]);

export default function FamilyDiscoveries({ members, events, treeSlug, onPostEvent }) {
  const [q, setQ] = useState("");

  const { milestones, birthdays, remembrances } = useMemo(() => {
    const ms = [], bd = [], rm = [];
    for (const m of members || []) {
      const name = [m.first_name, m.last_name].filter(Boolean).join(" ");
      if (!m.death_date && m.birth_date && !m.birth_date_is_year_only) {
        const occ = nextOccurrence(m.birth_date);
        if (occ) {
          const turning = occ.date.getFullYear() - Number(m.birth_date.slice(0, 4));
          const row = { m, name, turning, ...occ };
          if (turning > 0 && (MILESTONES.has(turning) || turning % 5 === 0)) ms.push(row);
          else bd.push(row);
        }
      }
      if (m.death_date && !m.death_date_is_year_only) {
        const occ = nextOccurrence(m.death_date);
        if (occ && occ.days <= 90) rm.push({ m, name, years: occ.date.getFullYear() - Number(m.death_date.slice(0, 4)), ...occ });
      }
    }
    const byDays = (a, b) => a.days - b.days;
    return { milestones: ms.sort(byDays), birthdays: bd.sort(byDays), remembrances: rm.sort(byDays) };
  }, [members]);

  const ql = q.trim().toLowerCase();
  const filt = (rows) => (ql ? rows.filter((r) => r.name.toLowerCase().includes(ql)) : rows);
  const filteredEvents = (events || []).filter((e) =>
    !ql || (e.title || "").toLowerCase().includes(ql) || (e.category || "").toLowerCase().includes(ql));

  const Person = ({ r, accent, badge }) => (
    <Link to={`/family/${treeSlug}/member/${r.m.id}`} className="rounded-xl border p-3 flex items-center gap-3 bg-white hover:shadow-md transition" style={{ borderColor: "var(--wofy-border)" }} data-testid={`discovery-person-${r.m.id}`}>
      <div className="w-11 h-11 rounded-full flex items-center justify-center font-display text-lg text-white shrink-0" style={{ background: accent }}>
        {badge}
      </div>
      <div className="min-w-0">
        <div className="font-semibold text-sm truncate">{r.name}</div>
        <div className="text-xs opacity-60">{fmt(r.date)} · {r.days === 0 ? "today!" : `in ${r.days} day${r.days === 1 ? "" : "s"}`}</div>
      </div>
    </Link>
  );

  return (
    <div className="space-y-8" data-testid="family-discoveries">
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 opacity-40" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search people or events…" className="w-full rounded-full border pl-9 pr-4 py-2 text-sm" style={{ borderColor: "var(--wofy-border)" }} data-testid="discoveries-search" />
        </div>
        <button onClick={onPostEvent} className="inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-semibold text-white" style={{ background: "#B25D38" }} data-testid="discoveries-post-event">
          <Plus className="w-4 h-4" /> Post an event
        </button>
      </div>

      {filt(milestones).length > 0 && (
        <section data-testid="discoveries-milestones">
          <h3 className="font-display text-xl mb-3 flex items-center gap-2"><Cake className="w-5 h-5" style={{ color: "#B25D38" }} /> Milestone birthdays</h3>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {filt(milestones).slice(0, 12).map((r) => <Person key={r.m.id} r={r} accent="#B25D38" badge={`${r.turning}`} />)}
          </div>
          <p className="text-[11px] opacity-60 mt-2">Golden jubilees, Sweet Sixteens and every fifth year — occasions the whole family gathers for.</p>
        </section>
      )}

      {filt(birthdays).length > 0 && (
        <section data-testid="discoveries-birthdays">
          <h3 className="font-display text-xl mb-3">Upcoming birthdays</h3>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {filt(birthdays).slice(0, 9).map((r) => <Person key={r.m.id} r={r} accent="#0891B2" badge={<Cake className="w-5 h-5" />} />)}
          </div>
        </section>
      )}

      {filt(remembrances).length > 0 && (
        <section data-testid="discoveries-remembrance">
          <h3 className="font-display text-xl mb-3 flex items-center gap-2"><Flower2 className="w-5 h-5" style={{ color: "#7C3AED" }} /> Remembrance days (next 90 days)</h3>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {filt(remembrances).slice(0, 9).map((r) => <Person key={r.m.id} r={r} accent="#7C3AED" badge={<Flower2 className="w-5 h-5" />} />)}
          </div>
        </section>
      )}

      <section data-testid="discoveries-events">
        <h3 className="font-display text-xl mb-3 flex items-center gap-2"><Calendar className="w-5 h-5" /> Family events</h3>
        {filteredEvents.length === 0 ? (
          <div className="wofy-card text-sm opacity-70">
            No events {ql ? "match your search" : "posted yet"} — weddings, First Communions, graduations, engagements, house-warmings… post one for the family!
          </div>
        ) : (
          <div className="space-y-2">
            {filteredEvents.slice(0, 20).map((e) => (
              <div key={e.id} className="rounded-xl border bg-white p-3 flex items-center justify-between gap-3" style={{ borderColor: "var(--wofy-border)" }} data-testid={`discovery-event-${e.id}`}>
                <div className="min-w-0">
                  <div className="font-semibold text-sm truncate">{e.title}</div>
                  <div className="text-xs opacity-60">{e.date || e.start_date || ""}{e.category ? ` · ${e.category.replace(/_/g, " ")}` : ""}</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
